import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, errorMessage } from "../../api/client";
import { useCompany, useDuplicates } from "../../api/hooks";
import type { CompanyDetail, Duplicate } from "../../api/types";
import { Chip, Empty, Icon, PageHead, Segmented, Spinner, useToast } from "../../components/ui";
import { display, siret } from "../../lib/format";
import { useAuth } from "../../lib/auth";

const REASONS: Record<string, string> = {
  siret_identique: "SIRET identique", raison_sociale_identique: "Raison sociale identique", meme_code_postal: "Même code postal",
  telephone_commun: "Téléphone commun", meme_siren: "Même SIREN", email_identique: "Email identique", mobile_identique: "Mobile identique",
  nom_prenom_identiques: "Nom et prénom identiques", prenom_abrege: "Prénom abrégé",
};
const reason = (r: string) => REASONS[r] ?? r.replace(/_(\d+)$/, " ($1 %)").replace(/_/g, " ");
const ROWS: [keyof CompanyDetail, string][] = [
  ["siret", "SIRET"], ["name", "Raison sociale"], ["address_line4", "Adresse"], ["postal_code", "Code postal"], ["city", "Ville"],
  ["naf_code", "Code NAF"], ["headcount_band", "Effectif"], ["campaign_names", "Campagnes"],
];

function Pair({ d, onDone }: { d: Duplicate; onDone: () => void }) {
  const a = useCompany(d.left_id);
  const b = useCompany(d.right_id);
  const me = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const act = async (decision: "merge" | "reject", winner?: number) => {
    setBusy(true);
    try {
      await api(`/duplicates/${d.id}/resolve`, { json: { decision, winner_id: winner ?? null } });
      toast(decision === "merge" ? "Fiches fusionnées · la fiche absorbée reste consultable dans l'historique" : "Marqué comme « pas un doublon »");
      onDone();
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setBusy(false);
    }
  };
  if (a.isLoading || b.isLoading) return <div className="tile"><Spinner /></div>;
  // Juste après une fusion, la fiche absorbée renvoie { merged_into_id } avant que la liste ne soit rafraîchie.
  if (!a.data?.contacts || !b.data?.contacts) return <div className="tile"><span className="muted">Une des fiches n'est plus disponible (fusionnée ou supprimée).</span></div>;
  const A = a.data;
  const B = b.data;
  const val = (c: CompanyDetail, k: keyof CompanyDetail) => (k === "siret" ? siret(c.siret) : display(c[k]));
  const sirenConflict = !!(A.siret && B.siret && A.siret !== B.siret);
  return (
    <div className="tile">
      <div className="thead">
        <div className="row">
          <Chip tone={d.score >= 80 ? "orange" : "gray"} solid={d.score >= 80}>{Math.round(d.score)} %</Chip>
          {d.reasons.reasons.map((r) => <Chip key={r} tone="blue">{reason(r)}</Chip>)}
          {d.reasons.conflicts.map((r) => <Chip key={r} tone="red" dot>Conflit : {r.replace(/_/g, " ")}</Chip>)}
        </div>
        {d.import_id && <span className="muted" style={{ fontSize: 13 }}>détecté par l'import #{d.import_id}</span>}
      </div>
      <div className="cmp" role="table" aria-label="Comparaison des deux fiches">
        <div className="k hd" /><div className="hd">Fiche #{A.id}</div><div className="hd">Fiche #{B.id}</div>
        {ROWS.map(([k, l]) => {
          const va = val(A, k);
          const vb = val(B, k);
          const diff = va !== vb && va !== "–" && vb !== "–";
          return [
            <div key={`${k}k`} className="k">{l}</div>,
            <div key={`${k}a`} className={diff ? "diff" : ""}>{va}</div>,
            <div key={`${k}b`} className={diff ? "diff" : ""}>{vb}</div>,
          ];
        })}
        <div className="k">Contacts</div><div>{A.contacts.length}</div><div>{B.contacts.length}</div>
      </div>
      {me.can("data.merge") && (
        <div className="row between">
          <button className="btn" type="button" onClick={() => act("reject")} disabled={busy}>Pas un doublon</button>
          <div className="row">
            {sirenConflict ? <span className="error">Deux SIRET différents : établissements distincts, fusion impossible.</span> : (
              <>
                <button className="btn" type="button" onClick={() => act("merge", A.id)} disabled={busy}><Icon name="merge" />Garder #{A.id}</button>
                <button className="btn primary" type="button" onClick={() => act("merge", B.id)} disabled={busy}><Icon name="merge" />Garder #{B.id}</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function DuplicatesPage() {
  const [status, setStatus] = useState("pending");
  const list = useDuplicates(status);
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ["duplicates"] });
    qc.invalidateQueries({ queryKey: ["companies"] });
    qc.invalidateQueries({ queryKey: ["company"] });
  };
  return (
    <main className="page">
      <PageHead eyebrow="Master Data" title="Doublons à revoir"
        right={<Segmented label="Statut" value={status} onChange={setStatus} options={[["pending", "À traiter"], ["merged", "Fusionnés"], ["rejected", "Rejetés"]]} />} />
      <div className="banner info"><Icon name="alert" /><span>Fusion non destructive : les champs vides sont complétés, les valeurs différentes sont tracées dans l'historique, contacts et coordonnées sont regroupés. La fiche absorbée reste conservée.</span></div>
      {list.isLoading && <Spinner />}
      {list.data?.length === 0 && <div className="tile"><Empty title={status === "pending" ? "Aucun doublon en attente" : "Rien ici"}>{status === "pending" ? "Les imports signalent ici les rapprochements incertains (score entre 50 et 94 % ou conflit)." : ""}</Empty></div>}
      {status === "pending" ? list.data?.map((d) => <Pair key={d.id} d={d} onDone={done} />) : list.data && list.data.length > 0 && (
        <div className="tile flush"><table className="table"><thead><tr><th>Paire</th><th className="r">Score</th><th>Raisons</th></tr></thead>
          <tbody>{list.data.map((d) => <tr key={d.id}><td>#{d.left_id} ↔ #{d.right_id}</td><td className="r num">{Math.round(d.score)} %</td><td>{d.reasons.reasons.map(reason).join(", ")}</td></tr>)}</tbody></table></div>
      )}
    </main>
  );
}
